import React, { useRef } from "react";
import {
  IconButton,
  Link,
  makeStyles,
  Menu,
  MenuItem,
  TableCell,
  TableRow,
  Tooltip,
  Typography
} from "@material-ui/core";
import {
  Close,
  KeyboardArrowDown,
  KeyboardArrowUp,
  MoreVert,
  OpenInNew,
  PlaylistAddCheck,
  SaveAlt,
  Warning
} from "@material-ui/icons";
import locale from "@bonliva-crm/locale";
import {
  ConsultantContractTableRowDataFragment,
  ConsultantContractType,
  LegalPersonType,
  PublishConsultantContract
} from "@graphql";
import { store } from "@store";
import { toMultiLine } from "./utils";
import { ContractUploaderDialog } from "@consultants/ConsultantContract/ContractUploaderDialog";
import { openBlank } from "@clientUtils";
import {
  BC_contractsTypes,
  getConfirmationContractType,
  National_contractsTypes
} from "@consultants/ConsultantContract/utils";
import { ConsultantContractPensionSolution } from "@bonliva-crm/shared";
import { getConsultantAgeGroup } from "../../../../../../../libs/shared/src/lib/consultant-contract-strategy/utils";
import moment from "moment";

type Props = {
  data: ConsultantContractTableRowDataFragment;
  open?: boolean;

  onAddContractType: (id, ref) => void;
  onCollapse?: () => void;
  onCreateFrom: (id) => void;
  onDelete: (id) => void;
  onDownload: (link: string) => void;
  onDuplicate: (id) => void;
  onEdit: (id) => void;
  onEmail: (id) => void;
  onNullify: (id, value) => void;
  onSign: (id) => void;
  onUpload: (id, ref, row) => void;

  disabled: boolean;
};

export const ConsultantContractTableRow = (props: Props) => {
  const { data } = props;

  const classes = useStyles({});
  const [publishing, setPublishing] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const rowRef = useRef(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuClick = (cb) => {
    cb(data._id, rowRef, data);
    handleClose();
  };

  const handleNullify = (value: boolean) => {
    props.onNullify(data._id, value);
    handleClose();
  };

  const handlePublish = () => {
    setPublishing(true);

    PublishConsultantContract.func({
      contractId: data._id
    })
      .then(({ data }) => {
        const url = data.publishContract.contractUrl;
        openBlank(url);
        store().snackbars.success("Published");
      })
      .catch((e) => {
        store().snackbars.error(`Cannot publish contract<br>${e}`);
      })
      .finally(() => {
        setPublishing(false);
        handleClose();
      });
  };

  const isConfirmation =
    data.Type === ConsultantContractType.ConfirmationForEmployees ||
    data.Type === ConsultantContractType.Confirmation ||
    data.Type === ConsultantContractType.Confirmation_National ||
    data.Type === ConsultantContractType.ConfirmationForEmployees_National ||
    data.Type === ConsultantContractType.BC_Framework_Confirmation ||
    data.Type === ConsultantContractType.BC_Intermittent_Confirmation;

  const isFrameworkOrIntermittent =
    data.Type === ConsultantContractType.Intermittent ||
    data.Type === ConsultantContractType.Intermittent_National ||
    data.Type === ConsultantContractType.BC_Intermittent ||
    data.Type === ConsultantContractType.BC_Framework ||
    data.Type === ConsultantContractType.Framework ||
    data.Type === ConsultantContractType.Framework_National;

  const confirmationType = getConfirmationContractType(data.Type);
  const { is25To65 } = getConsultantAgeGroup(
    moment(data.Consultant.BirthDate).format("YYYY-MM-DD"),
    moment(data.StartDate).format("YYYY-MM-DD"),
    moment(data.EndDate).format("YYYY-MM-DD")
  );

  const salaryChoiceNotNeeded =
    (National_contractsTypes.includes(data.Type) &&
      (is25To65 || data.pensionSolution === ConsultantContractPensionSolution.Full)) ||
    isConfirmation;

  const isSalaryChoiceError =
    !salaryChoiceNotNeeded &&
    !data.SalaryChoice &&
    data.IsSigned &&
    data.Consultant?.LegalPersonType === LegalPersonType.Employee &&
    !BC_contractsTypes.includes(data.Type);

  return (
    <TableRow ref={rowRef} className={classes.row}>
      <TableCell style={{ width: "25px" }}>
        {isSalaryChoiceError && (
          <Tooltip title={locale.ErrorSalaryChoice} placement={"top"}>
            <Warning color={"error"} />
          </Tooltip>
        )}
      </TableCell>
      <TableCell component="th" scope="row">
        {toMultiLine(data.CustomerName)}
        {props.onCollapse && (
          <IconButton aria-label="expand row" size="small" onClick={props.onCollapse}>
            {/* eslint-disable-next-line no-restricted-globals */}
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        )}
      </TableCell>
      <TableCell>{data.CustomerCity}</TableCell>
      <TableCell>
        <Tooltip
          title={data.Period && `${locale.PeriodBooked}: ${data.Period}`}
          placement={"top"}
        >
          <Typography variant="body2" className={classes.dates}>
            {data.Dates}
          </Typography>
        </Tooltip>
      </TableCell>
      <TableCell>
        <div className={classes.iconWrap}>
          {locale.consultantContractTypes[data.Type]}
          {data.IsSigned && !data.IsNullified && <PlaylistAddCheck color={"secondary"} />}
          {data.IsNullified && <Close color="primary" />}
        </div>
      </TableCell>
      <TableCell style={{ width: "65px" }}>{data.SalaryChoiceAbbr}</TableCell>
      <TableCell style={{ width: "45px" }}>{data.PayoutsAbbr}</TableCell>
      <TableCell>
        <div className={classes.actions}>
          <IconButton onClick={() => props.onDownload(data.FileUrl)} size="small">
            <SaveAlt />
          </IconButton>
          <IconButton size="small">
            <Link href={data.ViewUrl} target="_blank" className={classes.link}>
              <OpenInNew />
            </Link>
          </IconButton>
          <IconButton
            key={`menu-${data._id}`}
            aria-controls="menu"
            aria-haspopup="true"
            size="small"
            onClick={handleClick}
            disabled={props.disabled}
          >
            <MoreVert />
          </IconButton>

          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
            <MenuItem
              onClick={() => handleMenuClick(props.onEdit)}
              disabled={!data.canEdit}
            >
              {locale.Edit}
            </MenuItem>

            {isConfirmation && (
              <MenuItem
                onClick={() => handleMenuClick(props.onSign)}
                disabled={data.IsSigned}
              >
                {locale.ApprovedByConsultant}
              </MenuItem>
            )}

            {!isConfirmation && (
              <MenuItem
                onClick={() => handleMenuClick(props.onUpload)}
                disabled={data.IsSigned}
              >
                {locale.UploadSignedDocument}
              </MenuItem>
            )}

            <MenuItem onClick={() => handleMenuClick(props.onDuplicate)}>
              {locale.Duplicate}
            </MenuItem>

            {isConfirmation && (
              <MenuItem onClick={() => handleMenuClick(props.onEmail)}>
                Skicka till konsult
              </MenuItem>
            )}

            {isSalaryChoiceError && (
              <MenuItem onClick={() => handleMenuClick(props.onAddContractType)}>
                {locale.AddSalaryChoice}
              </MenuItem>
            )}

            {isFrameworkOrIntermittent && (
              <MenuItem
                onClick={() => handleMenuClick(props.onCreateFrom)}
                disabled={!data.IsSigned}
              >
                {locale.Add} {locale.consultantContractTypes[confirmationType]}
              </MenuItem>
            )}

            {data.IsSigned && data.IsNullified && (
              <MenuItem onClick={() => handleNullify(false)} disabled={!data.canNullify}>
                {locale.UnNullify}
              </MenuItem>
            )}

            {data.IsSigned && !data.IsNullified && (
              <MenuItem onClick={() => handleNullify(true)}>{locale.Nullify}</MenuItem>
            )}

            {data.PublishedUrl && (
              <MenuItem
                onClick={() => openBlank(data.PublishedUrl)}
                disabled={data.IsSigned || data.IsNullified || publishing}
              >
                Visa i OneFlow
              </MenuItem>
            )}

            {data.canPublishToOneflowDirectly && (
              <MenuItem
                onClick={handlePublish}
                disabled={data.IsSigned || data.IsNullified || publishing}
              >
                {data.PublishedUrl ? "Återpublicera till OneFlow" : "Skicka till OneFlow"}
              </MenuItem>
            )}

            <MenuItem onClick={() => handleMenuClick(props.onDelete)}>
              {locale.Delete}
            </MenuItem>
          </Menu>

          <ContractUploaderDialog open={publishing} />
        </div>
      </TableCell>
    </TableRow>
  );
};

const useStyles = makeStyles(() => ({
  iconWrap: {
    display: "flex",
    alignItems: "center",
    "& svg": {
      marginLeft: 5
    }
  },
  link: {
    display: "inherit"
  },
  actions: {
    minWidth: "90px",
    textAlign: "right"
  },
  dates: {
    maxHeight: 75,
    whiteSpace: "nowrap",
    overflow: "hidden"
  },
  row: {
    "&:last-child > td, &:last-child th": {
      borderBottom: "none"
    }
  }
}));

type ArchiveProps = {
  title: React.ReactNode;
  open?: boolean;
  onCollapse?: () => void;
};

export const ArchiveRow: React.FC<ArchiveProps> = ({ title, open, onCollapse }) => {
  return (
    <TableRow onClick={onCollapse}>
      <TableCell colSpan={8}>
        <div
          style={{
            display: "flex",
            width: "100%",
            padding: "8px",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "none"
          }}
        >
          <Typography variant={"h6"}>{title}</Typography>
          {onCollapse && (
            <IconButton aria-label="expand row" size="small">
              {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};
